import fetch from "isomorphic-fetch";
import { generatePublicClientToken } from "../authUtils";
import { extractTrackingData } from "../trackingUtils";

const RETRYABLE_HTTP_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
const EMAIL_VERIFICATION_PENDING_KEY_PREFIX = "firebase_signup_email_verification_pending";
const RETRYABLE_CREATE_AUTH_USER_ERROR_CODES = new Set([
  "AUC_2001_FIREBASE_ADMIN_INIT_FAILED",
  "AUC_2002_FIREBASE_CREATE_USER_FAILED",
  "AUC_2006_FIREBASE_SIGNIN_FAILED",
  "AUC_3001_TOKEN_MISSING",
  "AUC_3002_TOKEN_DECODE_FAILED",
  "AUC_3003_DECODED_TOKEN_MISSING",
  "AUC_4001_AUTH_USER_CREATE_FAILED",
  "AUC_5001_ACCESS_TOKEN_CREATE_FAILED",
  "AUC_7001_ONBOARDING_CREATE_FAILED"
]);
const TERMINAL_CREATE_AUTH_USER_ERROR_CODES = new Set([
  "AUC_6001_BLOCKLISTED_USER",
  "AUC_6002_BANNED_USER"
]);
const CREATE_AUTH_USER_RETRYABLE_FALLBACK_MESSAGE = "We couldn't complete signup right now. Please try again.";
const CREATE_AUTH_USER_NON_RETRYABLE_FALLBACK_MESSAGE = "We couldn't create your account with those details.";
const CREATE_AUTH_USER_RETRY_LABEL = "Please retry.";

const normalizeErrorCode = (errorCode) => (
  typeof errorCode === "string" ? errorCode.toUpperCase() : ""
);

const isRetryableCreateAuthenticatedUserFailure = (status, errorCode) => {
  const normalizedErrorCode = normalizeErrorCode(errorCode);
  if (normalizedErrorCode) {
    return RETRYABLE_CREATE_AUTH_USER_ERROR_CODES.has(normalizedErrorCode);
  }

  return RETRYABLE_HTTP_STATUSES.has(status) || status >= 500;
};

const resolveCreateAuthenticatedUserFailureMessage = ({ status, errorCode, message }) => {
  if (message) return message;

  const retryable = isRetryableCreateAuthenticatedUserFailure(status, errorCode);
  return retryable ? CREATE_AUTH_USER_RETRYABLE_FALLBACK_MESSAGE : CREATE_AUTH_USER_NON_RETRYABLE_FALLBACK_MESSAGE;
};

const createAuthenticatedUserRetryLabel = () => CREATE_AUTH_USER_RETRY_LABEL;
const isCreateAuthenticatedUserTerminalFailure = (errorCode) => (
  TERMINAL_CREATE_AUTH_USER_ERROR_CODES.has(normalizeErrorCode(errorCode))
);
const emailVerificationPendingKey = (subject) => `${EMAIL_VERIFICATION_PENDING_KEY_PREFIX}:${subject}`;
const markEmailVerificationPending = (subject) => {
  if (typeof window === "undefined" || !window.sessionStorage) return;

  window.sessionStorage.setItem(emailVerificationPendingKey(subject), "1");
};

const hasEmailVerificationPending = (subject) => {
  if (typeof window === "undefined" || !window.sessionStorage) return false;

  return window.sessionStorage.getItem(emailVerificationPendingKey(subject)) === "1";
};

const getAdType = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const googleAd = urlParams.get("_gl");
  const facebookAd = urlParams.get("fbclid");

  if (googleAd) return "google";
  if (facebookAd) return "facebook";

  return null;
};

const postCreateAuthenticatedUser = async (email, password, subject, gaClientId = "", gaSessionId = "") => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const affiliate = urlParams.get("affiliate");
  const adType = getAdType();

  return (
    await fetch("/authenticated_users", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").getAttribute("content")
      },
      body: JSON.stringify({
        email,
        password,
        user_kind: subject,
        affiliate,
        ad_type: adType,
        tracking_data: extractTrackingData(window.location.search),
        woohoo: {
          ga_client_id: gaClientId,
          ga_session_id: gaSessionId
        }
      })
    })
  );
};

const redirectToLogin = (territory, email) => {
  setTimeout(() => {
    const url = new URL(`${territory.sso_domain}/continue`);
    const params = url.searchParams;

    params.append("from", window.location.href);
    params.append("user", email);
    params.append("redirect", true);
    params.append("callback", true);

    window.location.href = url.toString();
  }, 3000);
};

const checkForExistingFirebaseUser = async (territory, email) => {
  try {
    const publicToken = generatePublicClientToken(territory.public_client_api_key, territory.current_time, territory.code);
    const response = await fetch(`${territory.public_host_domain}/api/v2/kookie/core/user_records?email=${email}`, {
      method: "GET",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json",
        "API-KEY": publicToken,
        "API-VERSION": "2"
      }
    });

    if (!response.ok) { return false; }

    const payload = await response.json();
    const exists = Array.isArray(payload.data) && payload.data.length > 0;

    return exists;
  } catch (e) {
    console.log(e);
    return false;
  }
};

const updateOnboardingEmail = async (id, email, password, subject) => {
  if (!id || id === "undefined") {
    return {
      success: false,
      message: "We couldn't verify your session. Please refresh the page and try again."
    };
  }

  try {
    const response = await fetch(`/authenticated_users/${id}/update_email`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").getAttribute("content")
      },
      body: JSON.stringify({
        authenticated_user: {
          email,
          password,
          subject,
          tracking_data: extractTrackingData(window.location.search)
        }
      })
    });

    const isJson = response.headers.get("content-type")?.includes("application/json");
    const data = isJson ? await response.json() : {};

    if (!response.ok) {
      if (process.env.NODE_ENV === "development") {
        console.error("[updateEmail Failure]", {
          status: response.status,
          statusText: response.statusText,
          data
        });
      }

      return {
        success: false,
        message: data.message || `Server error (${response.status}): ${response.statusText}`
      };
    }

    return data;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[updateEmail Exception]", error);
    }

    return {
      success: false,
      message: "A network error occurred. Please check your connection or try again later."
    };
  }
};

const checkForExistingHmsUser = async (territory, email) => {
  try {
    const publicToken = generatePublicClientToken(territory.public_client_api_key, territory.current_time, territory.code);
    const response = await fetch(`${territory.public_host_domain}/api/v2/users?email=${email}`, {
      method: "GET",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json",
        "API-KEY": publicToken,
        "API-VERSION": "2"
      }
    });

    if (!response.ok) { return false; }

    const payload = await response.json();
    const exists = Array.isArray(payload.data) && payload.data.length > 0;

    return exists;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export {
  redirectToLogin,
  checkForExistingFirebaseUser,
  checkForExistingHmsUser,
  postCreateAuthenticatedUser,
  updateOnboardingEmail,
  isRetryableCreateAuthenticatedUserFailure,
  resolveCreateAuthenticatedUserFailureMessage,
  createAuthenticatedUserRetryLabel,
  isCreateAuthenticatedUserTerminalFailure,
  markEmailVerificationPending,
  hasEmailVerificationPending
};
